import { useMemo } from 'react';

import { DatePicker, Drawer, Empty, Form, Input, Select, Tag } from 'antd';
import { useIntl } from 'react-intl';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumn } from '@src/components/Table';
import { useCopyToClipboard } from '@src/hooks/useCopyToClipboard';
import type { TotemWithCreator } from '@src/models/interfaces/totem.interface';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import { DateUtil } from '@src/packages/utils/dates';
import IntlMessages from '@src/util/IntlMessages';

import { useMarkingZoneDrawer } from './hooks/useMarkingZoneDrawer';
import styles from './styles.module.less';

interface MarkingZoneDrawerProps {
  drawer: ReturnType<typeof useMarkingZoneDrawer>;
}

interface TotemRowActionsProps {
  totem: TotemWithCreator;
  isRevoking: boolean;
  onDeactivate: (totem: TotemWithCreator) => void;
  onGoToPortal: (totem: TotemWithCreator) => void;
}

const TotemRowActions = ({
  totem,
  isRevoking,
  onDeactivate,
  onGoToPortal,
}: TotemRowActionsProps) => {
  const intl = useIntl();
  const { copied, copy } = useCopyToClipboard();
  const isExpired = totem.isExpired;

  return (
    <ActionButton.Container
      maxVisibleButtons={3}
      buttons={[
        <ActionButton
          key="copy"
          icon={copied ? 'check' : 'link'}
          disabled={isRevoking || isExpired || copied}
          tooltipText={intl.formatMessage({
            id: copied ? 'copyLink.copied' : 'workJourney.markingZone.copyLink',
          })}
          allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.CREATE}
          onClick={() => {
            void copy(totem.url);
          }}
        />,
        <ActionButton
          key="portal"
          icon="open_in_new"
          disabled={isRevoking || isExpired}
          tooltipText={intl.formatMessage({ id: 'workJourney.markingZone.goToPortal' })}
          allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.CREATE}
          onClick={() => onGoToPortal(totem)}
        />,
        <ActionButton
          key="deactivate"
          icon="delete"
          color="danger"
          disabled={isRevoking}
          tooltipText={intl.formatMessage({ id: 'workJourney.markingZone.delete' })}
          allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.DELETE}
          onClick={() => onDeactivate(totem)}
        />,
      ]}
    />
  );
};

const MarkingZoneDrawer = ({ drawer }: MarkingZoneDrawerProps) => {
  const intl = useIntl();
  const {
    isOpen,
    step,
    totems,
    loading,
    submitting,
    revokingJti,
    form,
    nameMaxLength,
    closeDrawer,
    handleFormFinish,
    handleGoToPortal,
    handleDeactivateClick,
    drawerActionButtons,
    formatActivationDate,
    formatExpiration,
    resolveTotemDisplayName,
    expirationOptions,
  } = drawer;

  const columns = useMemo(
    (): ResponsiveTableColumn<TotemWithCreator>[] => [
      {
        key: 'name',
        dataIndex: 'name',
        title: intl.formatMessage({ id: 'workJourney.markingZone.name' }),
        align: 'left',
        render: (totem: TotemWithCreator) => resolveTotemDisplayName(totem),
      },
      {
        key: 'status',
        dataIndex: 'isExpired',
        title: intl.formatMessage({ id: 'workJourney.markingZone.status' }),
        align: 'center',
        render: (totem: TotemWithCreator) =>
          totem.isExpired ? (
            <Tag color="orange">
              <IntlMessages id="workJourney.markingZone.status.expired" />
            </Tag>
          ) : (
            <Tag color="green">
              <IntlMessages id="workJourney.markingZone.status.active" />
            </Tag>
          ),
      },
      {
        key: 'creator',
        dataIndex: 'creatorDisplayName',
        title: intl.formatMessage({ id: 'workJourney.markingZone.supervisor' }),
        align: 'left',
        render: (totem: TotemWithCreator) => totem.creatorDisplayName,
      },
      {
        key: 'activation',
        dataIndex: 'createdAt',
        title: intl.formatMessage({ id: 'workJourney.markingZone.activation' }),
        align: 'left',
        render: (totem: TotemWithCreator) => formatActivationDate(totem.createdAt),
      },
      {
        key: 'expiration',
        dataIndex: 'expiresIn',
        title: intl.formatMessage({ id: 'workJourney.markingZone.expiration' }),
        align: 'left',
        render: (totem: TotemWithCreator) => formatExpiration(totem.expiresIn),
      },
      {
        key: 'actions',
        dataIndex: 'jti',
        title: '',
        align: 'center',
        columnType: 'actions',
        render: (totem: TotemWithCreator) => (
          <TotemRowActions
            totem={totem}
            isRevoking={revokingJti === totem.jti}
            onDeactivate={handleDeactivateClick}
            onGoToPortal={handleGoToPortal}
          />
        ),
      },
    ],
    [
      formatActivationDate,
      formatExpiration,
      handleDeactivateClick,
      handleGoToPortal,
      intl,
      resolveTotemDisplayName,
      revokingJti,
    ],
  );

  return (
    <Drawer
      open={isOpen}
      onClose={closeDrawer}
      closable={false}
      footer={null}
      title={null}
      width="45vw"
      className={styles.drawerRoot}
    >
      <BoxContainer>
        <BoxContainer content fixed bottomBorder>
          <FilterContainer
            goBack={closeDrawer}
            title={<Title.Header value={<IntlMessages id="workJourney.markingZone" />} />}
            actionButtons={drawerActionButtons}
          />
        </BoxContainer>

        <BoxContainer content loading={loading} className={styles.content}>
          <div className={styles.scrollArea}>
            {step === 'form' && (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormFinish}
                className={styles.formSection}
              >
                <Form.Item
                  name="name"
                  label={intl.formatMessage({ id: 'workJourney.markingZone.name' })}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message: intl.formatMessage({
                        id: 'workJourney.markingZone.nameRequired',
                      }),
                    },
                    {
                      max: nameMaxLength,
                      message: intl.formatMessage(
                        { id: 'workJourney.markingZone.nameMaxLength' },
                        { max: nameMaxLength },
                      ),
                    },
                  ]}
                >
                  <Input
                    maxLength={nameMaxLength}
                    placeholder={intl.formatMessage({
                      id: 'workJourney.markingZone.namePlaceholder',
                    })}
                    disabled={submitting}
                  />
                </Form.Item>

                <div className={styles.doubleField}>
                  <Form.Item
                    name="startAt"
                    label={intl.formatMessage({ id: 'workJourney.markingZone.startDate' })}
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: 'workJourney.markingZone.startDateRequired',
                        }),
                      },
                    ]}
                  >
                    <DatePicker
                      className="gx-w-100"
                      showTime={{ format: DateUtil.DEFAULT_TIME_FORMAT }}
                      format={`${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`}
                      placeholder={intl.formatMessage({ id: 'workJourney.markingZone.startDate' })}
                      disabled={submitting}
                    />
                  </Form.Item>

                  <div className={styles.expiresInField}>
                    <Form.Item
                      name="expiresIn"
                      label={intl.formatMessage({ id: 'workJourney.markingZone.expiresIn' })}
                    >
                      <Select
                        options={expirationOptions}
                        placeholder={intl.formatMessage({
                          id: 'workJourney.markingZone.expiresInPlaceholder',
                        })}
                        disabled={submitting}
                      />
                    </Form.Item>

                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, curr) => prev.expiresIn !== curr.expiresIn}
                    >
                      {({ getFieldValue }) =>
                        getFieldValue('expiresIn') == null ? (
                          <p className={`${styles.expiresInHint} gx-text-warning`}>
                            <IntlMessages id="workJourney.markingZone.neverExpiresWarning" />
                          </p>
                        ) : null
                      }
                    </Form.Item>
                  </div>
                </div>
              </Form>
            )}

            {step === 'list' && !loading && totems.length === 0 && (
              <div className={styles.emptyState}>
                <Empty description={<IntlMessages id="workJourney.markingZone.empty" />} />
              </div>
            )}

            {step === 'list' && totems.length > 0 && (
              <ResponsiveTable type="light" columns={columns} dataSource={totems} />
            )}
          </div>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

export default MarkingZoneDrawer;
